import { Link } from 'react-router-dom'
import { P, H, OL, UL, Li, Callout, Code, Screenshot } from '../components'

// ─── Last verified: 16 May 2026 (2 passes) ──────────────────────────────────
// Verified against:
//   - supabase/functions/send-email/index.ts
//     FROM_ADDRESS defaults to 'Receipt Chaser <noreply@receiptchaser.com>'
//     reply_to is NOT set on client_request emails (only contact form)
//   - src/pages/RequestDetail.js
//     Top-row buttons: "🔗 Copy client link", "📧 Send email"
//     No "Change recipient" feature; no per-request email override
//   - src/lib/supabase.js getRequestByToken: tokens don't expire; access
//     governed by cancelled_at and clients.archived
// Removed:
//   - "From line is 'Practice Name (via Receipt Chaser)'" -- actually
//     "Receipt Chaser <noreply@receiptchaser.com>". The practice name is
//     in the email subject and body, not the From line.
//   - "Change recipient" step -- no such feature.
//   - "Resend link" button -- actual label is "Send email" (re-sends the
//     original request email).
//   - "Copy client link from top right menu" -- the button is directly
//     on the request page header, not in a menu.
// Pass 2 added:
//   - Replies don't reach the bookkeeper (no reply-to header on request
//     emails) -- client should be told to reply through normal channels
//   - The upload link itself doesn't expire (until cancelled/deleted)
//   - Updating the client email address only affects future requests;
//     existing tokens stay tied to whichever address the email was sent to

export default {
  title:   "My client says they didn't get the email",
  summary: 'Spam folder checks, address typos, resending, and what to do when nothing seems to work.',
  body: (
    <>
      <P>
        Nine times out of ten, the email did arrive and is sitting in
        the spam folder. The rest of the time it's a typo in the
        address or a corporate email filter. This guide walks through
        the checks in the order that resolves the most cases fastest.
      </P>

      <H>Step 1: Check the recipient address against your records</H>
      <P>
        Before asking the client to dig, double-check that the email
        address on the client's profile is correct. A single typo
        (<em>sara@</em> instead of <em>sarah@</em>, or <em>.co</em>{' '}
        instead of <em>.com</em>) is the most common cause of "I
        didn't get it" reports.
      </P>
      <OL>
        <Li>Open the client from your dashboard.</Li>
        <Li>
          Compare the email address on file with one you know works
          (for example, a recent email they sent you directly).
        </Li>
        <Li>If it's wrong, jump to Step 4 below.</Li>
      </OL>

      <H>Step 2: Ask the client to check spam or junk</H>
      <P>
        Suggest they search their inbox for <em>"Receipt Chaser"</em>
        {' '}or <em>"noreply@receiptchaser.com"</em>. Searching catches
        the email whether it's in inbox, spam, promotions, or any
        other folder.
      </P>
      <P>
        Once they find it, ask them to mark it as "not spam" or move
        it to their inbox. This trains their email provider not to
        filter future emails, including any reminders that come
        later.
      </P>

      <Callout variant="tip" title="Add to address book">
        Most email clients have a "Add sender to address book" or
        "Mark as safe sender" option. If your clients do this once on
        the first email they get, every future request and reminder
        lands in their inbox without filtering.
      </Callout>

      <Callout variant="info" title="Replies don't reach you">
        Worth telling the client up front: the email comes from{' '}
        <Code>noreply@receiptchaser.com</Code>. If they reply to it,
        nobody reads the reply. If they have questions, they need to
        contact you through your normal channel (your own email, a
        text, a call).
      </Callout>

      <H>Step 3: Re-send the request email</H>
      <P>
        Sometimes emails get lost in transit and a fresh send fixes
        it. From the request page, click{' '}
        <Code>📧 Send email</Code> at the top. The same request email
        goes out again to the same address, using your usual request
        template.
      </P>

      <H>Step 4: Fix a wrong address</H>
      <P>
        Receipt Chaser doesn't have a per-request "change recipient"
        option. To resend to a different address:
      </P>
      <OL>
        <Li>
          Open the client's detail page and click{' '}
          <Code>Edit details</Code>. Update the email address. This
          change applies to all future requests for this client.
        </Li>
        <Li>
          Back on the open request, click <Code>📧 Send email</Code>.
          The email now goes to the new address (Receipt Chaser pulls
          the current email each time it sends).
        </Li>
      </OL>
      <P>
        The upload link itself doesn't change. It's tied to the
        request, not to the email address. Whoever has the link can
        use it.
      </P>

      <H>Step 5: Share the link directly</H>
      <P>
        If the email is reliably not getting through (corporate
        filtering is the usual culprit), skip email and send the link
        through whichever channel actually reaches them:
      </P>
      <OL>
        <Li>Open the request from your dashboard.</Li>
        <Li>
          Click <Code>🔗 Copy client link</Code> at the top. The link
          is copied to your clipboard.
        </Li>
        <Li>
          Paste it into a text, WhatsApp, your own email, or any other
          channel.
        </Li>
      </OL>

      <Callout variant="warning" title="Treat the link like a password">
        The link contains a unique token. Anyone who has it can upload
        files to that specific request. Don't post it publicly. Sending
        it directly to your client through a private channel is fine.
      </Callout>

      <H>Step 6: Corporate email filtering</H>
      <P>
        Some company email systems aggressively filter automated
        emails from unfamiliar domains. If the client has searched
        their whole inbox (including spam) and genuinely can't find
        the email, this is the likely cause.
      </P>
      <P>
        Two routes:
      </P>
      <UL>
        <Li>
          <strong>Ask the client to whitelist us.</strong> Have them
          forward the email (if they can find a previous one) to
          their IT team and ask them to add{' '}
          <Code>noreply@receiptchaser.com</Code> to the allowed list.
        </Li>
        <Li>
          <strong>Use a different email for this client.</strong>{' '}
          Switch the email on their profile to a personal address
          (Gmail, Hotmail, etc.) that they actually check, and Step 5
          (share the link directly) as a fallback if even that fails.
        </Li>
      </UL>

      <H>If all your clients are missing emails</H>
      <P>
        That's a different kind of problem. If you've checked multiple
        clients and the emails aren't reaching anyone, it might be a
        wider issue with our email delivery. Send us a message via
        the <Link to="/contact">Contact</Link> page and we'll take a
        look.
      </P>

      <H>Related guides</H>
      <UL>
        <Li>
          <Link to="/support/resending-or-editing-a-request">Resending or editing a request</Link>
        </Li>
        <Li>
          <Link to="/support/branding-client-emails">Branding your client-facing emails</Link>
        </Li>
        <Li>
          <Link to="/support/client-cant-upload">A client can't upload a file</Link>
        </Li>
      </UL>
    </>
  ),
}
